﻿import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { TrashIcon } from '@heroicons/react/24/outline';

const statusBadge = (status) => {
  if (status === 'PAGA') return 'badge-success';
  if (status === 'VENCIDA' || status === 'CANCELADA') return 'badge-danger';
  if (status === 'PARCIAL') return 'badge-warning';
  return 'badge-info';
};

const recoveryBadge = (status) => {
  if (status === 'RECUPERADO') return 'badge-success';
  if (status === 'PROMESSA') return 'badge-warning';
  if (status === 'EM_NEGOCIACAO') return 'badge-info';
  if (status === 'ENCERRADO' || status === 'CANCELADO') return 'badge-danger';
  return 'badge-info';
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value);
  return dt.toLocaleString('pt-BR');
};

const formatDateBr = (value) => {
  if (!value) return '-';
  const text = String(value);
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return text;
  return dt.toLocaleDateString('pt-BR');
};

const getTodayIso = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ChargeDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [charge, setCharge] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recovery, setRecovery] = useState(null);
  const [loadingRecovery, setLoadingRecovery] = useState(false);
  const [syncingRecovery, setSyncingRecovery] = useState(false);
  const [asaasAction, setAsaasAction] = useState('');
  const [paymentForm, setPaymentForm] = useState({
    data_pagamento: getTodayIso(),
    valor_pago: '',
    forma_pagamento: 'pix',
    observacoes: '',
    notificar_asaas: false,
  });

  const loadRecovery = async () => {
    setLoadingRecovery(true);
    try {
      const { data } = await axios.get(`/api/collections/charge/${id}`);
      setRecovery(data || null);
    } catch (error) {
      if (error?.response?.status === 404) {
        setRecovery(null);
      } else {
        toast.error(error.response?.data?.error || 'Erro ao carregar dados de recuperacao');
      }
    } finally {
      setLoadingRecovery(false);
    }
  };

  const loadDetail = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`/api/charges/${id}`);
      setCharge(data || null);
      const isAsaasLinked =
        String(data?.provider_name || '').toUpperCase() === 'ASAAS' && !!data?.provider_reference_id;
      setPaymentForm((old) => ({
        ...old,
        notificar_asaas: isAsaasLinked ? true : false,
      }));
      await loadRecovery();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar cobranca');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDetail();
  }, [id]);

  const syncRecoveryCase = async () => {
    setSyncingRecovery(true);
    try {
      await axios.post('/api/collections/engine/sync', {
        source_module: charge?.source_module || '',
      });
      toast.success('Motor de recuperacao sincronizado para esta carteira');
      await loadRecovery();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao sincronizar recuperacao');
    } finally {
      setSyncingRecovery(false);
    }
  };

  const registerPayment = async () => {
    try {
      if (!paymentForm.valor_pago) {
        toast.error('Informe valor do pagamento');
        return;
      }

      if (
        paymentForm.notificar_asaas
        && String(charge?.provider_name || '').toUpperCase() === 'ASAAS'
        && Number(paymentForm.valor_pago) + 0.009 < Number(charge?.saldo_aberto || 0)
      ) {
        toast.error('Para baixar no Asaas, informe o valor total em aberto. Para parcial, desmarque "Notificar Asaas".');
        return;
      }

      const response = await axios.post(`/api/charges/${id}/payments`, {
        data_pagamento: paymentForm.data_pagamento,
        valor_pago: Number(paymentForm.valor_pago),
        forma_pagamento: paymentForm.forma_pagamento,
        observacoes: paymentForm.observacoes || null,
        notificar_asaas: Boolean(paymentForm.notificar_asaas),
      });

      toast.success('Pagamento registrado');
      if (response?.data?.asaas?.notified) {
        if (response?.data?.asaas?.settled_in_cash) {
          toast.success('Pagamento informado no Asaas com sucesso');
        } else if (response?.data?.asaas?.already_received) {
          toast.success('Asaas ja constava com pagamento recebido');
        }
      }
      setPaymentForm({
        data_pagamento: getTodayIso(),
        valor_pago: '',
        forma_pagamento: 'pix',
        observacoes: '',
        notificar_asaas:
          String(charge?.provider_name || '').toUpperCase() === 'ASAAS' && !!charge?.provider_reference_id,
      });
      await loadDetail();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao registrar pagamento');
    }
  };

  const removePayment = async (payment) => {
    if (!payment?.id) return;
    const confirmed = window.confirm(`Deseja excluir o pagamento de R$ ${Number(payment.valor_pago || 0).toFixed(2)}?`);
    if (!confirmed) return;
    try {
      const { data } = await axios.delete(`/api/charges/${id}/payments/${payment.id}`);
      toast.success('Pagamento excluido');
      if (data?.asaas_notice) {
        toast(data.asaas_notice, { duration: 7000 });
      }
      await loadDetail();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao excluir pagamento');
    }
  };

  const runAction = async (action) => {
    try {
      if (action === 'cancel') {
        const motivo = window.prompt('Motivo do cancelamento:') || '';
        await axios.post(`/api/charges/${id}/cancel`, { motivo });
      } else if (action === 'delete') {
        const confirmed = window.confirm('Deseja excluir esta cobranca? Esta acao nao pode ser desfeita.');
        if (!confirmed) return;
        await axios.delete(`/api/charges/${id}`);
        toast.success('Cobranca excluida com sucesso');
        navigate('/cobrancas');
        return;
      } else if (action === 'renegotiate') {
        const data = window.prompt('Nova data de vencimento (YYYY-MM-DD):');
        if (!data) return;
        await axios.post(`/api/charges/${id}/renegotiate`, { data_vencimento: data });
      } else {
        await axios.post(`/api/charges/${id}/${action}`);
      }
      toast.success('Acao executada com sucesso');
      await loadDetail();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao executar acao');
    }
  };

  const issueAsaas = async (billingType) => {
    const alreadyIssued = !!charge?.provider_reference_id;
    const forceNew = alreadyIssued && window.confirm('Esta cobranca ja possui referencia no Asaas. Deseja reemitir?');
    if (alreadyIssued && !forceNew) return;

    setAsaasAction(`issue_${billingType}`);
    try {
      await axios.post(`/api/charges/${id}/asaas/issue`, {
        billing_type: billingType,
        force_new: forceNew,
      });
      toast.success(`Cobranca emitida no Asaas (${billingType})`);
      await loadDetail();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao emitir cobranca no Asaas');
    } finally {
      setAsaasAction('');
    }
  };

  const syncAsaas = async () => {
    setAsaasAction('sync');
    try {
      await axios.post(`/api/charges/${id}/asaas/sync`);
      toast.success('Cobranca sincronizada com Asaas');
      await loadDetail();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao sincronizar com Asaas');
    } finally {
      setAsaasAction('');
    }
  };

  const undoAsaasReceive = async () => {
    if (!charge?.provider_reference_id) return;
    const confirmed = window.confirm('Deseja desfazer o recebimento no Asaas para esta cobranca?');
    if (!confirmed) return;

    setAsaasAction('undo_receive');
    try {
      const { data } = await axios.post(`/api/charges/${id}/asaas/undo-receive`);
      const removed = Number(data?.asaas?.local_payments_removed || 0);
      if (removed > 0) {
        toast.success(`Recebimento desfeito no Asaas e ${removed} pagamento(s) local(is) removido(s)`);
      } else if (data?.asaas?.already_open) {
        toast.success('Asaas ja indicava esta cobranca sem recebimento confirmado');
      } else {
        toast.success('Recebimento desfeito no Asaas');
      }
      await loadDetail();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao desfazer recebimento no Asaas');
    } finally {
      setAsaasAction('');
    }
  };

  if (loading && !charge) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="loader h-10 w-10" />
      </div>
    );
  }

  if (!charge) {
    return <div className="text-gray-500">Cobranca nao encontrada.</div>;
  }

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <button className="text-primary-600 hover:text-primary-800 text-sm mb-2" onClick={() => navigate('/cobrancas')}>
            Voltar para cobrancas
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Cobranca #{charge.id.slice(0, 8)}</h1>
          <p className="text-gray-600">Cliente: {charge.customer_nome || '-'}</p>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary" onClick={() => runAction('emit')}>
            Emitir
          </button>
          <button className="btn-secondary" onClick={() => runAction('send')}>
            Marcar Enviada
          </button>
          <button className="btn-secondary" onClick={() => runAction('renegotiate')}>
            Renegociar
          </button>
          <button className="btn-danger" onClick={() => runAction('cancel')}>
            Cancelar
          </button>
          <button className="btn-danger" onClick={() => runAction('delete')}>
            Excluir
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Status</p>
            <span className={statusBadge(charge.status)}>{charge.status}</span>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Valor Total Dinamico</p>
            <p className="text-xl font-semibold">R$ {Number(charge.valor_total_calculado || 0).toFixed(2)}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Valor Pago</p>
            <p className="text-xl font-semibold">R$ {Number(charge.valor_pago_total || 0).toFixed(2)}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Saldo Aberto</p>
            <p className="text-xl font-semibold">R$ {Number(charge.saldo_aberto || 0).toFixed(2)}</p>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header flex flex-wrap items-center justify-between gap-3">
          <span>Recuperacao Inteligente</span>
          <div className="flex gap-2">
            <button className="btn-secondary" onClick={() => navigate('/cobrancas/recuperacao')}>
              Abrir Pipeline
            </button>
            <button className="btn-primary" onClick={syncRecoveryCase} disabled={syncingRecovery}>
              {syncingRecovery ? 'Sincronizando...' : 'Sincronizar Caso'}
            </button>
          </div>
        </div>
        <div className="card-body">
          {loadingRecovery ? (
            <div className="py-6 flex justify-center">
              <div className="loader h-8 w-8" />
            </div>
          ) : recovery ? (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-500">Status do Caso</p>
                <span className={recoveryBadge(recovery.status)}>{recovery.status}</span>
              </div>
              <div>
                <p className="text-sm text-gray-500">Risco</p>
                <p className="text-xl font-semibold">{Number(recovery.risco_score || 0).toFixed(1)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Proxima acao</p>
                <p className="font-medium">{formatDateTime(recovery.next_action_scheduled_for || recovery.next_action_at)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Promessa ativa</p>
                {recovery.open_promise_id ? (
                  <p className="font-medium">
                    {formatDateBr(recovery.open_promise_due_date)} - R$ {Number(recovery.open_promise_amount || 0).toFixed(2)}
                  </p>
                ) : (
                  <p className="text-gray-400">Sem promessa</p>
                )}
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-600">
              Esta cobranca ainda nao possui caso no motor de recuperacao. Clique em "Sincronizar Caso" para gerar.
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header flex flex-wrap items-center justify-between gap-3">
          <span>Integracao Asaas</span>
          <div className="flex flex-wrap gap-2">
            <button className="btn-secondary" onClick={() => issueAsaas('PIX')} disabled={!!asaasAction}>
              {asaasAction === 'issue_PIX' ? 'Gerando PIX...' : 'Gerar PIX'}
            </button>
            <button className="btn-secondary" onClick={() => issueAsaas('BOLETO')} disabled={!!asaasAction}>
              {asaasAction === 'issue_BOLETO' ? 'Gerando Boleto...' : 'Gerar Boleto'}
            </button>
            <button className="btn-primary" onClick={syncAsaas} disabled={!!asaasAction || !charge.provider_reference_id}>
              {asaasAction === 'sync' ? 'Sincronizando...' : 'Sincronizar'}
            </button>
            <button className="btn-secondary" onClick={undoAsaasReceive} disabled={!!asaasAction || !charge.provider_reference_id}>
              {asaasAction === 'undo_receive' ? 'Desfazendo...' : 'Desfazer Recebimento'}
            </button>
          </div>
        </div>
        <div className="card-body grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="space-y-2">
            <p className="text-sm text-gray-500">Provedor</p>
            <p className="font-medium">{charge.provider_name || '-'}</p>
            <p className="text-sm text-gray-500 mt-2">Referencia Asaas</p>
            <p className="font-mono text-sm break-all">{charge.provider_reference_id || '-'}</p>
            <p className="text-sm text-gray-500 mt-2">Status no Provedor</p>
            <p className="font-medium">{charge.provider_status || '-'}</p>
          </div>

          <div className="space-y-2">
            <p className="text-sm text-gray-500">PIX</p>
            {charge.pix_qr_image_url ? (
              <img src={charge.pix_qr_image_url} alt="QR Code PIX" className="w-40 h-40 border rounded-md bg-white p-2" />
            ) : (
              <p className="text-sm text-gray-400">QR Code ainda nao disponivel</p>
            )}
            <p className="text-xs break-all text-gray-700">{charge.pix_qr_text || '-'}</p>
          </div>

          <div className="space-y-2">
            <p className="text-sm text-gray-500">Boleto</p>
            <p className="text-xs break-all text-gray-700">Codigo de barras: {charge.boleto_barcode || '-'}</p>
            <p className="text-xs break-all text-gray-700">Linha digitavel: {charge.boleto_linha_digitavel || '-'}</p>
            {charge.boleto_pdf_url ? (
              <a href={charge.boleto_pdf_url} target="_blank" rel="noreferrer" className="text-primary-600 hover:text-primary-800 text-sm font-medium">
                Abrir PDF do boleto
              </a>
            ) : (
              <p className="text-sm text-gray-400">PDF do boleto nao disponivel</p>
            )}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">Registrar Pagamento</div>
        <div className="card-body grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="form-label">Data</label>
            <input
              className="form-input"
              type="date"
              value={paymentForm.data_pagamento}
              onChange={(e) => setPaymentForm({ ...paymentForm, data_pagamento: e.target.value })}
            />
          </div>
          <div>
            <label className="form-label">Valor</label>
            <input
              className="form-input"
              type="number"
              step="0.01"
              value={paymentForm.valor_pago}
              onChange={(e) => setPaymentForm({ ...paymentForm, valor_pago: e.target.value })}
            />
          </div>
          <div>
            <label className="form-label">Forma</label>
            <select className="form-select" value={paymentForm.forma_pagamento} onChange={(e) => setPaymentForm({ ...paymentForm, forma_pagamento: e.target.value })}>
              <option value="pix">PIX</option>
              <option value="boleto">Boleto</option>
              <option value="transferencia">Transferencia</option>
              <option value="cartao">Cartao</option>
              <option value="dinheiro">Dinheiro</option>
              <option value="outro">Outro</option>
            </select>
          </div>
          <div className="flex items-end">
            <button className="btn-primary w-full" onClick={registerPayment}>
              Registrar
            </button>
          </div>
          <div className="md:col-span-4">
            <label className="form-label">Observacoes</label>
            <input className="form-input" value={paymentForm.observacoes} onChange={(e) => setPaymentForm({ ...paymentForm, observacoes: e.target.value })} />
          </div>
          {String(charge?.provider_name || '').toUpperCase() === 'ASAAS' && !!charge?.provider_reference_id && (
            <div className="md:col-span-4">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={Boolean(paymentForm.notificar_asaas)}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notificar_asaas: e.target.checked })}
                />
                Notificar Asaas que o pagamento foi recebido fora da plataforma (dinheiro/PIX estatico)
              </label>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="card-header">Itens da Cobranca</div>
          <div className="card-body p-0">
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Descricao</th>
                    <th>Qtd</th>
                    <th>Unitario</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(charge.items || []).map((item) => (
                    <tr key={item.id}>
                      <td>{item.descricao}</td>
                      <td>{Number(item.quantidade).toFixed(2)}</td>
                      <td>R$ {Number(item.valor_unitario).toFixed(2)}</td>
                      <td>R$ {Number(item.valor_total).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">Pagamentos</div>
          <div className="card-body p-0">
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Valor</th>
                    <th>Forma</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(charge.payments || []).map((item) => (
                    <tr key={item.id}>
                      <td>{item.data_pagamento}</td>
                      <td>R$ {Number(item.valor_pago).toFixed(2)}</td>
                      <td>{item.forma_pagamento}</td>
                      <td>
                        <button
                          className="text-danger-600 hover:text-danger-900 p-1"
                          onClick={() => removePayment(item)}
                          title="Excluir pagamento"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {(charge.payments || []).length === 0 && (
                    <tr>
                      <td colSpan={4} className="text-center text-gray-500 py-6">
                        Sem pagamentos
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">Historico de Eventos</div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Evento</th>
                  <th>Descricao</th>
                </tr>
              </thead>
              <tbody>
                {(charge.events || []).map((event) => (
                  <tr key={event.id}>
                    <td>{new Date(event.created_at).toLocaleString('pt-BR')}</td>
                    <td>{event.tipo_evento}</td>
                    <td>{event.descricao || '-'}</td>
                  </tr>
                ))}
                {(charge.events || []).length === 0 && (
                  <tr>
                    <td colSpan={3} className="text-center text-gray-500 py-6">
                      Sem historico
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChargeDetailPage;




